import { createContext, useContext } from 'react';
import { ELinxConnectionMethod, EmbedUrlRequest } from 'vopay';

// Caller-facing subset of the embed request — the fixed fields (RedirectURL/RedirectMethod) and the
// ClientAccountID/ContactID branch are filled in by the provider.
export type ELinxConnectRequest = Omit<
  EmbedUrlRequest,
  'RedirectURL' | 'RedirectMethod' | 'ClientAccountID' | 'ContactID' | 'PaymentSelectionMethod'
> & {
  PaymentSelectionMethod: ELinxConnectionMethod;
};

// Raises the overlay from the raw inputs; resolves with the linked token (rejects if closed first). The
// host-specific values (country/clientAccountsEnabled) are supplied by the caller — a host's thin adapter
// typically binds them from its own account context so its callers don't have to.
export type OpenELinxConnect = (
  values: ELinxConnectRequest,
  country: EmbedUrlRequest['Country'],
  clientAccountsEnabled: boolean,
  accountID?: string,
) => Promise<string | undefined>;

export const ELinxConnectContext = createContext<{ open: OpenELinxConnect } | null>(null);

// Pass-through accessor for the shared-owned eLinx flow (mirrors useDialog/useAlertDialog). The overlay
// is rendered once by ELinxConnectProvider (composed into SharedProviders). Hosts usually consume this
// through a thin adapter that binds country/clientAccountsEnabled — see client-dashboard's useELinxConnect.
export default function useELinxConnect() {
  const eLinxConnect = useContext(ELinxConnectContext);

  if (!eLinxConnect) {
    throw new Error("useELinxConnect: no ELinxConnectProvider mounted. Mount <SharedProviders> (from 'shared').");
  }

  return eLinxConnect;
}
